import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { isExpectedDomRemovalError } from './lib/errors'
import packageJson from '../package.json'

import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/300.css'
import '@fontsource/inter/800.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/montserrat/400.css'
import '@fontsource/montserrat/500.css'
import '@fontsource/montserrat/600.css'
import '@fontsource/montserrat/700.css'
import '@fontsource/montserrat/800.css'
import '@fontsource/montserrat/900.css'

import './index.css'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Create a new router instance
const router = createRouter({ routeTree })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const isProduction = import.meta.env.MODE === 'production'
const defaultTracesSampleRate = isProduction ? 0.1 : 1.0
const tracesSampleRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
  ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
  : defaultTracesSampleRate

if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || `sabhia-hub@${packageJson.version}`,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: tracesSampleRate,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}
const app = <RouterProvider router={router} />

const rootElement = document.getElementById('root')!

const root = createRoot(rootElement, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    if (isExpectedDomRemovalError(error)) {
      if (import.meta.env.DEV) {
        console.debug('[Expected DOM Error]', error instanceof Error ? error.message : error)
      }
      return
    }

    const stack = errorInfo?.componentStack
    console.error('[Uncaught Error]', error, stack)
  }),
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    if (isExpectedDomRemovalError(error)) {
      return
    }

    if (import.meta.env.DEV) {
      const stack = errorInfo?.componentStack
      console.warn('[Caught Error]', error, stack)
    }
  }),
  onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
    if (isExpectedDomRemovalError(error)) {
      return
    }

    if (import.meta.env.DEV) {
      const stack = errorInfo?.componentStack
      console.info('[Recoverable Error]', error, stack)
    }
  }),
})

root.render(import.meta.env.DEV ? <StrictMode>{app}</StrictMode> : app)
